import React from "react";
import "./styles.css";

/**
 * @function UIButton
 * @param {STRING} className - Additional classes to add to the UIButton
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {React.Component} children - React components that's being wrapped by the UIButton component.
 * @param {dataPoint} dataPoint - data-point to add to wrapper for captures. 
 * @return {React.Component}
 * @exported true
 * @description The UIButton is the base component for all of the button components. It is used as a blueprint.
 * @example
 * <UIButton className="added-style">Activate<UIButton>
 */
export default function UIButton(props) {
  const className = props.className ?? "";
  let dataPoint = props.dataPoint ? { "data-point": props.dataPoint } : {};

  return (
    <button className={className + " clickable ui-button flex-width"} onClick={props.onClick}>
      <span {...dataPoint}>{props.children}</span>
    </button>
  );
}

/**
 * @function UIPrimaryButton
 * @param {STRING} className - Additional classes to add to the UIPrimaryButton
 * @param {STRING} text - Passes the button text to the UIButton
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {dataPoint} dataPoint - data-point to add to wrapper for captures.
 * @return {React.Component}
 * @exported true
 * @description This is the primary button option. This has a colored background with an inverted text color.
 * @example
 * <UIPrimaryButton className="added-style" text="Activate" onClick={function} />
 */
export function UIPrimaryButton(props) {
  const className = props.className ?? "";
  const text = props.text ?? "[text]";

  return (
    <UIButton className={className + " ui-primary-button"} onClick={props.onClick} dataPoint={props.dataPoint}>
      <span className="primary-button-text">{text}</span>
    </UIButton>
  );
}

/**
 * @function UISecondaryButton
 * @param {STRING} className - Additional classes to add to the UISecondaryButton
 * @param {STRING} text - Passes the button text to the UIButton
 * @param {dataPoint} dataPoint - data-point to add to wrapper for captures.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @return {React.Component}
 * @exported true
 * @description This button has a border and no semi-transparent colored background. It is used as the secondary button option. 
 * @example
 * <UISecondaryButton className="added-style" text="Edit" onClick={function} />
 */
export function UISecondaryButton(props) {
  const className = props.className ?? "";
  const text = props.text ?? "[text]";

  return (
    <UIButton className={className + " ui-secondary-button"} onClick={props.onClick} dataPoint={props.dataPoint}>{text}</UIButton>
  );
}